import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";

interface CommentDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  actionLabel: string;
  onConfirm: (comment: string, isPublic: boolean) => void;
  isLoading?: boolean;
  elevated?: boolean;
}

export function CommentDialog({
  isOpen,
  onOpenChange,
  title,
  description,
  actionLabel,
  onConfirm,
  isLoading = false,
  elevated = false,
}: CommentDialogProps) {
  const [comment, setComment] = useState("");
  const [isPublic, setIsPublic] = useState(false);

  const handleConfirm = () => {
    onConfirm(comment, isPublic);
    setComment(""); // Reset comment after submission
    setIsPublic(false); // Reset checkbox after submission
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setComment(""); // Reset comment when dialog closes
      setIsPublic(false); // Reset checkbox when dialog closes
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent elevated={elevated} className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="comment" className="text-sm font-medium">
              Optional Note
            </label>
            <Textarea
              id="comment"
              placeholder="Add a note explaining your decision..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="note-visibility"
              checked={isPublic}
              onCheckedChange={(checked) => setIsPublic(checked === true)}
            />
            <Label htmlFor="note-visibility" className="text-sm">
              Make note visible to registrant
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? "Processing..." : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
